'use client';

import { useMemo } from 'react';

import { QuotedColorGroup } from '@ravago/shared/page-data/models/elements';
import { LeadTimeIndicator } from '@ravago/shared/page-data/models/elements/product-lead-time.model';
import { Box } from '@ravago/shared/radiance/components/box/components/Box/Box';
import { Button } from '@ravago/shared/radiance/components/button/components/Button/Button';
import { RadioCard } from '@ravago/shared/radiance/components/radio/components/RadioCard/RadioCard';
import { RadioCardGroup } from '@ravago/shared/radiance/components/radio/components/RadioCardGroup/RadioCardGroup';
import { Typography } from '@ravago/shared/radiance/components/typography/components/Typography/Typography';
import { useTranslations } from '@ravago/shared/translations/client';

import { LeadTimeBadge } from '../../../../shared/lead-time-badge/lead-time-badge';
import { hasQuotedColorGroupMtsLocalLeadTime } from '../../../../../utils/lead-time';
import { QuoteDeliveryAddressOption } from '../../hooks/use-order-now-v2-state';

import { SelectedColorHeader } from './selected-color-header';

interface Props {
   selectedColorGroup: QuotedColorGroup;
   deliveryAddressOptions: QuoteDeliveryAddressOption[];
   selectedDeliveryAddressId: number | undefined;
   hasMultipleSelections: boolean;
   leadTimeIndicators: LeadTimeIndicator[];
   leadTimeEnabled: boolean;
   bracketPricingEnabled?: boolean;
   onEdit: () => void;
   onSelectDeliveryAddress: (deliveryAddressId: number) => void;
   onContinue: () => void;
}

export const QuoteDeliveryAddressSelectionView = ({
   selectedColorGroup,
   deliveryAddressOptions,
   selectedDeliveryAddressId,
   hasMultipleSelections,
   leadTimeIndicators,
   leadTimeEnabled,
   bracketPricingEnabled = false,
   onEdit,
   onSelectDeliveryAddress,
   onContinue
}: Props) => {
   const translate = useTranslations();

   const addressMtsLocalMap = useMemo(() => {
      if (!leadTimeEnabled) return {};

      return deliveryAddressOptions.reduce((acc, address) => {
         acc[address.id] = hasQuotedColorGroupMtsLocalLeadTime(leadTimeIndicators, selectedColorGroup, {
            bracketPricingEnabled,
            addressId: address.id
         });
         return acc;
      }, {} as Record<number, boolean>);
   }, [deliveryAddressOptions, selectedColorGroup, leadTimeIndicators, leadTimeEnabled, bracketPricingEnabled]);
   const showLeadTimeIndicator = Object.values(addressMtsLocalMap).some(Boolean);

   return (
      <Box direction="column">
         <SelectedColorHeader
            colorName={selectedColorGroup.colorName}
            producerColorCode={selectedColorGroup.producerColorCode}
            hasMultipleSelections={hasMultipleSelections}
            onEdit={onEdit}
         />

         <Box direction="column" gap="xs">
            <Typography variant="body-highlight" color="subtle">
               {translate('request-for-quotation.delivery-address')}
            </Typography>

            <RadioCardGroup
               value={selectedDeliveryAddressId?.toString() ?? ''}
               onChange={(value) => onSelectDeliveryAddress(Number(value))}
            >
               {deliveryAddressOptions.map((deliveryAddress) => (
                  <RadioCard key={deliveryAddress.id} value={deliveryAddress.id.toString()}>
                     <Box direction="row" justify="between" items="center">
                        <Box direction="column" gap="3xs">
                           <Typography variant="small">{deliveryAddress.formattedAddress}</Typography>
                        </Box>
                        {showLeadTimeIndicator && <LeadTimeBadge isMtsLocal={addressMtsLocalMap[deliveryAddress.id]} />}
                     </Box>
                  </RadioCard>
               ))}
            </RadioCardGroup>
         </Box>

         <Button fullWidth disabled={selectedDeliveryAddressId === undefined} onClick={onContinue}>
            {translate('dynamic-product-block.select-delivery-address')}
         </Button>
      </Box>
   );
};
